import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, CheckCircle2, Info, MapPin, Calendar, HelpCircle, MessageSquare, UserPlus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { useBackNavigation } from "@/hooks/useBackNavigation";
import InstitutionsBar from "@/components/InstitutionsBar";
import { getCmsLayout, CmsLayout } from "@/data/cmsStore";
import { useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Facebook, Instagram } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const VoterPage = () => {
  const { t, isRtl, lang } = useI18n();
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const handleBack = useBackNavigation();
  const [layout, setLayout] = useState<CmsLayout>([]);

  useEffect(() => {
    setLayout(getCmsLayout("voter"));
    
    const handleLayoutChange = (e: any) => {
      if (e.detail?.page === "voter") {
        setLayout(getCmsLayout("voter"));
      }
    };
    window.addEventListener("cmsLayoutChanged", handleLayoutChange);
    return () => window.removeEventListener("cmsLayoutChanged", handleLayoutChange);
  }, []);

  const content = {
    fr: {
      guideTitle: "Le parcours de l'Électeur",
      guideSubtitle: "Les étapes essentielles pour exercer votre droit de vote.",
      step1Title: "1. S'inscrire sur les listes",
      step1Desc: "Rendez-vous à l'annexe administrative (Moqata'a) de votre lieu de résidence avant la date limite (du 15 mai au 13 juin 2026), muni de votre CIN.",
      step2Title: "2. Vérifier son inscription",
      step2Desc: "Envoyez votre numéro de CIN par SMS au 2727 (gratuit) ou vérifiez sur le portail listeselectorales.ma.",
      step3Title: "3. Le Jour du Vote",
      step3Desc: "Le jour du scrutin (23 septembre 2026), présentez-vous à votre bureau de vote avec votre Carte d'Identité Nationale. La carte d'électeur physique n'est plus obligatoire.",
      faqTitle: "Foire Aux Questions",
      faq1Q: "Dois-je avoir une carte d'électeur pour voter ?",
      faq1A: "Non, depuis les dernières réformes, la présentation de la Carte d'Identité Nationale (CIN) est suffisante pour voter, à condition d'être inscrit sur les listes électorales.",
      faq2Q: "Comment trouver l'annexe administrative la plus proche ?",
      faq2A: "Vous pouvez vous renseigner auprès du Caïdat ou de la Moqata'a dont relève votre lieu de résidence.",
      faq3Q: "Que faire si j'ai changé d'adresse ?",
      faq3A: "Vous devez formuler une demande de transfert d'inscription en ligne (listeselectorales.ma) ou auprès de votre nouvelle annexe administrative pendant la période de révision annuelle des listes, muni de votre CIN à jour ou d'un justificatif de domicile.",
      faq4Q: "Le vote est-il obligatoire au Maroc ?",
      faq4A: "Le vote est un droit et un devoir civique fondamental, mais il n'est pas obligatoire au Maroc et ne fait l'objet d'aucune sanction légale en cas d'abstention."
    },
    ar: {
      guideTitle: "مسار الناخب",
      guideSubtitle: "الخطوات الأساسية لممارسة حقك في التصويت.",
      step1Title: "1. التسجيل في اللوائح",
      step1Desc: "توجه إلى الملحقة الإدارية (المقاطعة) التابعة لمحل سكنك قبل الموعد النهائي (من 15 ماي إلى 13 يونيو 2026)، مصحوباً ببطاقتك الوطنية.",
      step2Title: "2. التحقق من التسجيل",
      step2Desc: "أرسل رقم بطاقتك الوطنية عبر رسالة نصية إلى 2727 (مجانًا) أو تحقق عبر البوابة listeselectorales.ma.",
      step3Title: "3. يوم التصويت",
      step3Desc: "يوم الاقتراع (23 سبتمبر 2026)، توجه إلى مكتب التصويت الخاص بك مصحوباً ببطاقتك الوطنية. بطاقة الناخب الورقية لم تعد إلزامية.",
      faqTitle: "أسئلة شائعة",
      faq1Q: "هل أحتاج إلى بطاقة ناخب للتصويت؟",
      faq1A: "لا، منذ التعديلات الأخيرة، يكفي الإدلاء ببطاقة التعريف الوطنية (CIN) للتصويت، شريطة أن تكون مسجلاً في اللوائح الانتخابية.",
      faq2Q: "كيف أعثر على أقرب ملحقة إدارية؟",
      faq2A: "يمكنك الاستفسار لدى القيادة أو المقاطعة التابعة لمحل سكنك.",
      faq3Q: "ماذا أفعل إذا غيرت عنوان سكني؟",
      faq3A: "يجب عليك تقديم طلب نقل التسجيل عبر الإنترنت (listeselectorales.ma) أو لدى الملحقة الإدارية الجديدة خلال فترة المراجعة السنوية للوائح، مصحوباً ببطاقتك الوطنية المحدثة أو شهادة السكنى.",
      faq4Q: "هل التصويت إلزامي في المغرب؟",
      faq4A: "التصويت حق وواجب وطني أساسي، ولكنه ليس إجبارياً في المغرب ولا يترتب عنه أي عقوبة قانونية في حالة الامتناع."
    },
    zgh: {
      guideTitle: "ⴰⴱⵔⵉⴷ ⵏ ⵓⵎⵙⵜⴰⵢ",
      guideSubtitle: "ⵜⵉⵙⴽⵉⵍⵉⵏ ⵜⵉⵎⵇⵇⵔⴰⵏⵉⵏ ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵜⵉⵜ.",
      step1Title: "1. ⴰⵙⵖⵍ ⴳ ⵜⵍⴳⴰⵎⵉⵏ",
      step1Desc: "ⴷⴷⵓ ⵖⵔ ⵜⵎⵙⵙⵓⴳⵓⵔⵜ ⵏ ⵜⵎⵏⴰⴹⵜ ⵏⵏⴽ ⵓⵔⵜⴰ ⵉⵣⵔⵉ ⵡⴰⴽⵓⴷ ⵉⵜⵜⵓⵙⵖⵜⵙⵏ (ⵙⴳ 15 ⵎⴰⵢ ⴰⵔ 13 ⵢⵓⵏⵢⵓ 2026), ⴰⵡⵉ ⵜⴰⴽⴰⵕⴹⴰ ⵏⵏⴽ ⵜⴰⵏⴰⵎⵓⵔⵜ.",
      step2Title: "2. ⴰⵙⴼⵇⵇⵔ ⵏ ⵓⵙⵖⵍ",
      step2Desc: "ⴰⵣⵏ ⵓⵟⵟⵓⵏ ⵏ ⵜⴽⴰⵕⴹⴰ ⵏⵏⴽ ⵙ SMS ⵖⵔ 2727 (ⴼⴰⴱⵓⵔ) ⵏⵖ ⵙⴼⵇⵇⵔ ⴳ ⵓⵙⵉⵜ listeselectorales.ma.",
      step3Title: "3. ⴰⵙⵙ ⵏ ⵓⵙⵜⴰⵢ",
      step3Desc: "ⴰⵙⵙ ⵏ ⵓⵙⵜⴰⵢ (23 ⵛⵓⵜⴰⵏⴱⵉⵔ 2026), ⴷⴷⵓ ⵖⵔ ⵓⵙⵉⵔⴰ ⵏ ⵓⵙⵜⴰⵢ ⵏⵏⴽ ⴰⵡⵉ ⵜⴰⴽⴰⵕⴹⴰ ⵏⵏⴽ. ⵜⴰⴽⴰⵕⴹⴰ ⵏ ⵓⵎⵙⵜⴰⵢ ⵓⵔ ⵜⴳⵉ ⵜⴰⵛⵛⵉⴼⵜ.",
      faqTitle: "ⵉⵙⵇⵙⵉⵜⵏ ⵉⵜⵜⵓⵙⴰⵡⴰⵍⵏ",
      faq1Q: "ⵉⵙ ⵉⵅⵚⵚⴰ ⴰⴷ ⵉⵍⵉⵖ ⵜⴰⴽⴰⵕⴹⴰ ⵏ ⵓⵎⵙⵜⴰⵢ ⴰⴼⴰⴷ ⴰⴷ ⵙⵜⵉⵖ?",
      faq1A: "ⵓⵀⵓ, ⵜⴰⴽⴰⵕⴹⴰ ⵜⴰⵏⴰⵎⵓⵔⵜ (CIN) ⵜⴰⴼⵉ ⴰⴼⴰⴷ ⴰⴷ ⵜⵙⵜⵉⵜ, ⵎⴽ ⵜⵜⵓⵔⴰⵜ ⴳ ⵜⵍⴳⴰⵎⵉⵏ.",
      faq2Q: "ⵎⴰⵏⵉ ⵖⴰ ⴰⴼⵖ ⵜⴰⵎⵙⵙⵓⴳⵓⵔⵜ ⵏ ⵜⵎⵏⴰⴹⵜ ⵉⵏⵓ?",
      faq2A: "ⵉⵖⵉⵢ ⴰⴷ ⵜⵙⵇⵙⵉⵜ ⴳ ⵍⵇⵉⵢⴰⴷⴰ ⵏⵖ ⵜⵎⵙⵙⵓⴳⵓⵔⵜ ⵏ ⵡⴰⵏⵙⴰ ⵏⵏⴽ.",
      faq3Q: "ⵎⴰ ⵖⴰ ⵙⴽⵔⵖ ⵎⴽ ⵙⵏⴼⵍⵖ ⴰⵏⵙⴰ ⵏ ⵜⵓⴷⵔⵜ ⵉⵏⵓ?",
      faq3A: "ⵉⵅⵚⵚⴰ ⴰⴷ ⵜⵙⴽⵔⵜ ⵜⵓⵜⵔⴰ ⵏ ⵓⵙⵏⴼⵍ ⴳ ⵡⴰⵏⵜⵉⵔⵏⵉⵜ (listeselectorales.ma) ⵏⵖ ⴳ ⵜⵎⵙⵙⵓⴳⵓⵔⵜ ⵜⴰⵎⴰⵢⵏⵓⵜ ⴳ ⵡⴰⴽⵓⴷ ⵏ ⵓⵙⵎⴰⵢⵏⵓ ⵏ ⵜⵍⴳⴰⵎⵉⵏ, ⴰⵡⵉ ⵜⴰⴽⴰⵕⴹⴰ ⵏⵏⴽ ⵏⵖ ⴰⵏⵥⴰ ⵏ ⵡⴰⵏⵙⴰ.",
      faq4Q: "ⵉⵙ ⵉⴳⴰ ⵓⵙⵜⴰⵢ ⴰⵛⵛⵉⴼ ⴳ ⵍⵎⵖⵔⵉⴱ?",
      faq4A: "ⴰⵙⵜⴰⵢ ⵉⴳⴰ ⴰⵣⵔⴼ ⴷ ⵓⵡⴰⵊⵉⴱ ⴰⵏⴰⵎⵓⵔ, ⵎⴰⵛⴰ ⵓⵔ ⵉⴳⵉ ⴰⵛⵛⵉⴼ ⴳ ⵍⵎⵖⵔⵉⴱ ⴷ ⵓⵔ ⵉⵍⵉ ⴰⵡⴷ ⵢⴰⵏ ⵓⵎⴰⴳⴳⵓⴳ ⴰⵣⵔⴼⴰⵏ ⵎⴽ ⵓⵔ ⵜⵙⵜⵉⵜ."
    }
  };

  const tLocal = lang === 'ar' ? content.ar : lang === 'zgh' ? content.zgh : content.fr;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 md:pt-24 pb-8">
        <div className="container max-w-4xl px-4 md:px-6">
          <Link to="/attentes-citoyennes" onClick={handleBack} className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <BackArrow className="w-4 h-4" />
            {t.common.backHome}
          </Link>

          {layout.length > 0 ? (
            layout.map((section) => {
              if (!section.visible) return null;
              
              switch (section.type) {
                case "VoterHeroSection":
                  return (
                    <motion.div
                      key={section.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <h1 className="text-2xl md:text-5xl font-display font-bold text-foreground mb-4">
                        {section.content[`title_${lang}`] || section.content.title_fr || t.voter.title}
                      </h1>
                      <div 
                        className="text-base md:text-lg text-muted-foreground font-body mb-6 md:mb-8 max-w-2xl"
                        dangerouslySetInnerHTML={{ __html: section.content[`subtitle_${lang}`] || section.content.subtitle_fr || t.voter.subtitle }}
                      />
                    </motion.div>
                  );

                case "VoterVerificationSection":
                  return (
                    <div key={section.id} className="grid md:grid-cols-2 gap-6 mb-8 mt-4">
            {/* Left Block: Online Portal */}
            <motion.div
              className="bg-card rounded-2xl border border-primary/20 shadow-lg p-6 md:p-8 relative overflow-hidden group flex flex-col justify-between"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-20 -mt-20 transition-transform group-hover:scale-110"></div>
              
              <div className="relative z-10 flex flex-col gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 shadow-inner">
                  <CheckCircle2 className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-2">
                    {t.voter.checkTitle}
                  </h2>
                  <div 
                    className="text-muted-foreground font-body text-sm md:text-base prose dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: section.content[`check_desc_${lang}`] || section.content.check_desc_fr || "Consultez le portail officiel des listes électorales pour vérifier votre inscription et trouver votre bureau de vote." }}
                  />
                </div>
              </div>
              <Button size="lg" className="gap-2 font-body font-semibold shadow-md w-full sm:w-auto self-start bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
                <a
                  href="https://www.listeselectorales.ma/fr/Recherche_BV_f.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.voter.checkBtn}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            </motion.div>

            {/* Right Block: SMS Verification */}
            <motion.div
              className="bg-card rounded-2xl border border-secondary/20 shadow-lg p-6 md:p-8 relative overflow-hidden group flex flex-col justify-between"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-secondary/5 rounded-full blur-3xl -mr-20 -mb-20 transition-transform group-hover:scale-110"></div>
              
              <div className="relative z-10 flex flex-col gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center shrink-0 shadow-inner">
                  <MessageSquare className="w-7 h-7 text-secondary" />
                </div>
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-2">
                    {isRtl ? "التحقق عبر رسالة (SMS)" : "Vérification par SMS"}
                  </h2>
                  <div 
                    className="text-muted-foreground font-body text-base md:text-lg prose dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: section.content[`sms_desc_${lang}`] || section.content.sms_desc_fr || (isRtl ? `أرسل ببساطة رقم <strong class="font-bold text-foreground">بطاقتك الوطنية (CIN)</strong> إلى <strong class="font-bold text-foreground text-xl">2727</strong>.` : `Envoyez simplement votre Numéro de <strong class="font-bold text-foreground">CIN</strong> au <strong class="font-bold text-foreground text-xl">2727</strong>.`) }}
                  />
                </div>
              </div>

              {/* Active SMS Button for all devices */}
              <Button size="lg" variant="secondary" className="gap-2 font-body font-semibold shadow-md w-full sm:w-auto self-start" asChild>
                <a href="sms:2727">
                  <MessageSquare className="w-4 h-4" />
                  {isRtl ? "إرسال رسالة نصية (2727)" : "Envoyer un SMS au 2727"}
                </a>
              </Button>
              </motion.div>
            </div>
          );

        case "VoterTimelineSection": {
          let steps = [];
          try {
            steps = JSON.parse(section.content.steps || "[]");
          } catch(e) { steps = []; }
          
          if (steps.length === 0) {
            // fallback for backward compatibility
            steps = [
              {
                id: "step1",
                title_fr: tLocal.step1Title, title_ar: tLocal.step1Title, title_zgh: tLocal.step1Title,
                desc_fr: section.content[`step1_desc_${lang}`] || section.content.step1_desc_fr || tLocal.step1Desc
              },
              {
                id: "step2",
                title_fr: tLocal.step2Title, title_ar: tLocal.step2Title, title_zgh: tLocal.step2Title,
                desc_fr: section.content[`step2_desc_${lang}`] || section.content.step2_desc_fr || tLocal.step2Desc
              },
              {
                id: "step3",
                title_fr: tLocal.step3Title, title_ar: tLocal.step3Title, title_zgh: tLocal.step3Title,
                desc_fr: section.content[`step3_desc_${lang}`] || section.content.step3_desc_fr || tLocal.step3Desc
              }
            ];
          }

          const timelineIcons = [UserPlus, Search, Calendar];

          return (
            <div key={section.id} className="grid md:grid-cols-5 gap-8 mt-8">
              <motion.div
                className="md:col-span-3 space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
              <div>
                <h2 className="text-2xl font-display font-bold text-foreground mb-2 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-primary" />
                  {tLocal.guideTitle}
                </h2>
                <p className="text-muted-foreground font-body">
                  {tLocal.guideSubtitle}
                </p>
              </div>

              <div className="relative pl-8 md:pl-0">
                {/* Timeline Line */}
                <div className={`hidden md:block absolute top-4 bottom-4 w-0.5 bg-border ${isRtl ? 'right-[27px]' : 'left-[27px]'}`}></div>

                <div className="space-y-8">
                  {steps.map((step: any, index: number) => {
                    const Icon = timelineIcons[index % timelineIcons.length];
                    const isPrimary = index % 2 === 0;
                    return (
                      <div key={step.id || index} className="relative flex flex-col md:flex-row gap-4 md:gap-6">
                        <div className="md:w-14 flex justify-center md:pt-1 relative z-10 shrink-0">
                          <div className={`w-14 h-14 rounded-full bg-background border-2 ${isPrimary ? 'border-primary' : 'border-secondary'} flex items-center justify-center shadow-sm absolute top-0 ${isRtl ? '-right-7 md:relative md:right-0' : '-left-7 md:relative md:left-0'}`}>
                            <Icon className={`w-6 h-6 ${isPrimary ? 'text-primary' : 'text-secondary'}`} />
                          </div>
                        </div>
                        <div className={`rounded-xl border p-5 shadow-sm flex-1 ${isPrimary ? 'bg-card border-border' : 'bg-secondary/5 border-secondary/20'}`}>
                          <h3 className={`font-display font-semibold text-lg mb-2 ${isPrimary ? '' : 'text-secondary'}`}>
                            {step[`title_${lang}`] || step.title_fr}
                          </h3>
                          <div 
                            className="text-muted-foreground font-body text-sm leading-relaxed prose dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: step[`desc_${lang}`] || step.desc_fr || '' }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </motion.div>

            </div>
          );
        }

        case "VoterFAQSection": {
          let faqs = [];
          try {
            faqs = JSON.parse(section.content.faqs || "[]");
          } catch(e) { faqs = []; }

          if (faqs.length === 0) {
            // fallback
            faqs = [
              { id: 'faq1', question_fr: tLocal.faq1Q, answer_fr: section.content[`faq1_a_${lang}`] || section.content.faq1_a_fr || tLocal.faq1A },
              { id: 'faq2', question_fr: tLocal.faq2Q, answer_fr: section.content[`faq2_a_${lang}`] || section.content.faq2_a_fr || tLocal.faq2A },
              { id: 'faq3', question_fr: tLocal.faq3Q, answer_fr: section.content[`faq3_a_${lang}`] || section.content.faq3_a_fr || tLocal.faq3A },
              { id: 'faq4', question_fr: tLocal.faq4Q, answer_fr: section.content[`faq4_a_${lang}`] || section.content.faq4_a_fr || tLocal.faq4A }
            ];
          }

          return (
            <div key={section.id} className="grid md:grid-cols-5 gap-8 mt-8">
              <div className="md:col-span-3"></div>
              <motion.div
                className="md:col-span-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="bg-card rounded-2xl border border-border shadow-sm p-6 sticky top-24">
                  <h3 className="text-xl font-display font-bold text-foreground mb-6 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-secondary" />
                    {tLocal.faqTitle}
                  </h3>
                  
                  <Accordion type="single" collapsible className="w-full">
                    {faqs.map((faq: any, index: number) => (
                      <AccordionItem key={faq.id || index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left font-body font-semibold text-sm hover:no-underline hover:text-primary transition-colors">
                          {faq[`question_${lang}`] || faq.question_fr}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground text-sm leading-relaxed prose dark:prose-invert max-w-none">
                          <div dangerouslySetInnerHTML={{ __html: faq[`answer_${lang}`] || faq.answer_fr || '' }} />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </motion.div>
            </div>
          );
        }

        case "CustomRichSection": {
          const langHtml = lang === "fr" ? section.content.html_fr : lang === "ar" ? section.content.html_ar : section.content.html_fr;
          return (
            <div key={section.id} className={`${section.content.bg_color || 'bg-background'} ${section.content.padding_y || 'py-12'}`}>
              <div dangerouslySetInnerHTML={{ __html: langHtml || '' }} />
            </div>
          );
        }

        default:
          return null;
      }
    })
  ) : (
    <div className="h-64 flex items-center justify-center">
      <span className="animate-pulse">Chargement...</span>
    </div>
  )}
        </div>
      </div>
      <InstitutionsBar />
      <Footer />
    </div>
  );
};

export default VoterPage;
